import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type { SupportTicketPageApiResponse } from "../supportTicketTypes";
import { parseSupportTicketApiError } from "../utils/parseSupportTicketApiError";
import { SUPPORT_TICKETS_DEFAULT_PAGE_SIZE } from "./fetchSupportTickets";

/**
 * Request body for POST /pharmacy/support-tickets/search.
 */
export interface SearchSupportTicketsParams {
  org_id: string;
  q?: string;
  status?: string;
  priority?: string;
  /** True returns only archived tickets; false (default) returns active tickets. */
  is_archived?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Searches support tickets by text and optional filters.
 *
 * @param params - Organization id, search query, filters, and pagination.
 * @returns Paginated matching ticket rows.
 */
export const searchSupportTickets = async (
  params: SearchSupportTicketsParams,
): Promise<SupportTicketPageApiResponse> => {
  const body: Record<string, unknown> = {
    org_id: params.org_id,
    is_archived: params.is_archived ?? false,
    page: params.page ?? 1,
    limit: params.limit ?? SUPPORT_TICKETS_DEFAULT_PAGE_SIZE,
  };

  const query = params.q?.trim();
  if (query) {
    body.q = query;
  }

  if (params.status) {
    body.status = params.status;
  }

  if (params.priority) {
    body.priority = params.priority;
  }

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.SEARCH_SUPPORT_TICKETS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        await parseSupportTicketApiError(response, "Failed to search support tickets"),
      );
    }

    const result = (await response.json()) as SupportTicketPageApiResponse;
    logDataToConsole("searchSupportTickets", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in searchSupportTickets");
    throw error;
  }
};
